// ============================================================
// components/landing/AndroidWaitList.tsx
//
// The Android wait list: a button that opens a sign-up dialog
// (optional name, required phone or email). Shown in place of
// App Store badges on Android (see BestOnIPhone.tsx and
// LandingLayout.tsx). The referral code says which screen a
// sign-up came from; pages override the home default with
// <AndroidReferralProvider>.
// ============================================================
import React, { createContext, useContext, useEffect, useId, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { ANDROID_CTA, ANDROID_HEADLINE, ANDROID_OPT_IN, ANDROID_REFERRAL, DBIP_ATTRIBUTION, browserTimeZone } from '../../lib/android';
import { subscribeAndroidWaitList } from '../../services/android-wait-list-service';

const AndroidReferralContext = createContext<string>(ANDROID_REFERRAL.home);
export const AndroidReferralProvider = AndroidReferralContext.Provider;

const inputClass =
  'h-11 w-full rounded-xl border border-input bg-background px-3 text-base text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

function AndroidWaitListDialog({ onClose }: { onClose: () => void }): React.JSX.Element {
  const referralCode = useContext(AndroidReferralContext);
  const titleId = useId();
  const nameId = useId();
  const contactId = useId();
  const nameRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState('');
  const [contact, setContact] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => nameRef.current?.focus(), []);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!contact.trim()) {
      setError('Enter a phone number or email so we can let you know.');
      return;
    }
    setError('');
    setBusy(true);
    const timeZone = browserTimeZone();
    const result = await subscribeAndroidWaitList({
      ...(name.trim() ? { name: name.trim() } : {}),
      contact: contact.trim(),
      referralCode,
      ...(timeZone ? { timeZone } : {}),
    });
    setBusy(false);
    if (result.ok) setDone(true);
    else setError(result.message);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.key === 'Escape' && onClose()}
        className="relative w-full max-w-md rounded-3xl border bg-card p-6 text-left text-card-foreground shadow-2xl"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3 top-3 inline-flex h-11 w-11 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <X className="h-5 w-5" aria-hidden="true" />
        </button>
        <p className="mb-1 mt-0 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--stash-green)]">Android</p>
        <h2 id={titleId} className="mb-4 mr-10 mt-0 text-xl font-bold leading-snug">{ANDROID_HEADLINE}</h2>

        {done ? (
          <div>
            <p className="mb-5 mt-0 leading-relaxed"><strong>You’re on the list.</strong> We’ll let you know as soon as STASH is on Android.</p>
            <button type="button" onClick={onClose} className="inline-flex h-11 items-center rounded-full border px-5 font-semibold hover:bg-muted">Close</button>
          </div>
        ) : (
          <form onSubmit={submit} noValidate>
            <p className="mb-4 mt-0 font-semibold">{ANDROID_CTA}.</p>
            <label htmlFor={nameId} className="mb-1 block text-sm font-semibold">
              Name <span className="font-normal text-muted-foreground">(optional)</span>
            </label>
            <input id={nameId} ref={nameRef} name="name" autoComplete="name" maxLength={100} value={name} onChange={(e) => setName(e.target.value)} className={`${inputClass} mb-3`} />
            <label htmlFor={contactId} className="mb-1 block text-sm font-semibold">Phone or Email</label>
            <input
              id={contactId}
              name="contact"
              required
              inputMode="email"
              autoComplete="email"
              maxLength={200}
              placeholder="(615) 555-0123 or you@example.com"
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              className={`${inputClass} mb-3`}
            />
            <p className="mb-4 mt-0 text-xs leading-relaxed text-muted-foreground">
              {ANDROID_OPT_IN}{' '}
              <a href={DBIP_ATTRIBUTION.url} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">
                {DBIP_ATTRIBUTION.label}
              </a>
            </p>
            {error && <p role="alert" className="mb-4 mt-0 text-sm font-semibold text-destructive">{error}</p>}
            <button
              type="submit"
              disabled={busy}
              className="inline-flex h-11 items-center rounded-full bg-primary px-6 font-semibold text-primary-foreground shadow-sm hover:bg-[var(--stash-blue-dark)] disabled:opacity-60"
            >
              Notify me
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

/**
 * Opens the wait-list dialog. `variant="notice"` (in place of an App Store
 * badge) adds the iPhone-only line; `variant="header"` is the compact
 * "Get notified" pill for the site header.
 */
export function AndroidWaitListButton({
  variant = 'notice',
  className = '',
}: { variant?: 'notice' | 'header'; className?: string }): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const button =
    variant === 'header' ? (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`ml-1 inline-flex h-9 items-center rounded-full bg-primary px-4 text-sm font-semibold text-primary-foreground shadow-sm transition-all hover:bg-[var(--stash-blue-dark)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${className}`}
      >
        Get notified
      </button>
    ) : (
      <div className={`flex flex-col items-start gap-3 ${className}`}>
        <p className="m-0 max-w-sm font-semibold leading-snug">{ANDROID_HEADLINE}.</p>
        {/* The art sits on the button: centered on it, top corners rounded, its
            bottom edge shaded as if the button were in front of it. */}
        <div className="inline-flex flex-col items-center">
          <span className="relative block w-[6.25rem] overflow-hidden rounded-t-lg after:pointer-events-none after:absolute after:inset-x-0 after:bottom-0 after:h-3 after:bg-gradient-to-t after:from-black/35 after:to-transparent after:content-['']">
            <img src="/android-header.gif" alt="" width={100} height={56} loading="lazy" decoding="async" className="block h-auto w-full" />
          </span>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="inline-flex h-12 items-center rounded-full bg-primary px-6 font-semibold text-primary-foreground shadow-sm transition-all hover:bg-[var(--stash-blue-dark)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            {ANDROID_CTA}
          </button>
        </div>
      </div>
    );
  return (
    <>
      {button}
      {open && <AndroidWaitListDialog onClose={() => setOpen(false)} />}
    </>
  );
}
